import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';
import './AlpacaConfig.css';

// Fonction pour obtenir l'URL de l'API dynamiquement
const getApiUrlDynamic = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return '/api';
  }
  return 'http://localhost:8000/api';
};

const AlpacaConfig = () => {
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configs, setConfigs] = useState([]);
  const [limits, setLimits] = useState({ live: { max: 1, current: 0 }, paper: { max: 3, current: 0 } });
  const [editingConfig, setEditingConfig] = useState(null); // ID du config en cours d'édition, null pour nouveau
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    api_key_id: '',
    api_secret_key: '',
    base_url: 'https://paper-api.alpaca.markets',
    account_type: 'paper',
    sub_account_name: '',
    sell_threshold_up: 10.00,
    sell_threshold_down: -5.00,
    max_spend_amount: '',
    asset_source: 'top_gainers'
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError('');
      
      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/alpaca/config/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setConfigs(data.data || []);
        if (data.limits) {
          setLimits(data.limits);
        }
      } else {
        setError(data.message || 'Erreur lors du chargement des configurations');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des configurations:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      // Si on change base_url, mettre à jour account_type automatiquement
      if (name === 'base_url') {
        newData.account_type = value.includes('paper') ? 'paper' : 'live';
      }
      // Si on change account_type, mettre à jour base_url automatiquement
      if (name === 'account_type') {
        newData.base_url = value === 'live' ? 'https://api.alpaca.markets' : 'https://paper-api.alpaca.markets';
      }
      return newData;
    });
    setError('');
    setSuccess('');
  };

  const handleEdit = (config) => {
    setEditingConfig(config.id);
    setFormData({
      api_key_id: config.api_key_id || '',
      api_secret_key: '', // Ne jamais pré-remplir la clé secrète
      base_url: config.base_url || 'https://paper-api.alpaca.markets',
      account_type: config.account_type || 'paper',
      sub_account_name: config.sub_account_name || '',
      sell_threshold_up: config.sell_threshold_up ?? 10.00,
      sell_threshold_down: config.sell_threshold_down ?? -5.00,
      max_spend_amount: config.max_spend_amount !== null && config.max_spend_amount !== undefined ? config.max_spend_amount : '',
      asset_source: config.asset_source || 'top_gainers'
    });
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingConfig(null);
    setFormData({
      api_key_id: '',
      api_secret_key: '',
      base_url: 'https://paper-api.alpaca.markets',
      account_type: 'paper',
      sub_account_name: '',
      sell_threshold_up: 10.00,
      sell_threshold_down: -5.00,
      max_spend_amount: '',
      asset_source: 'top_gainers',
      position_check_interval_minutes: 5
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingConfig(null);
    setFormData({
      api_key_id: '',
      api_secret_key: '',
      base_url: 'https://paper-api.alpaca.markets',
      account_type: 'paper',
      sub_account_name: '',
      sell_threshold_up: 10.00,
      sell_threshold_down: -5.00,
      max_spend_amount: '',
      asset_source: 'top_gainers',
      position_check_interval_minutes: 5
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.api_key_id.trim()) {
      setError('L\'API Key ID est requis');
      setSaving(false);
      return;
    }

    // api_secret_key est requis pour créer un nouveau sous-compte
    if (!editingConfig && !formData.api_secret_key.trim()) {
      setError('L\'API Secret Key est requise pour créer un nouveau sous-compte');
      setSaving(false);
      return;
    }

    // Validation des seuils
    const thresholdUp = parseFloat(formData.sell_threshold_up);
    const thresholdDown = parseFloat(formData.sell_threshold_down);

    if (isNaN(thresholdUp) || thresholdUp <= 0 || thresholdUp > 100) {
      setError('Le seuil de vente en hausse doit être entre 0 et 100%');
      setSaving(false);
      return;
    }

    if (isNaN(thresholdDown) || thresholdDown >= 0 || thresholdDown < -100) {
      setError('Le seuil de vente en baisse doit être entre -100 et 0%');
      setSaving(false);
      return;
    }

    // Vérifier les limites
    if (!editingConfig) {
      if (formData.account_type === 'live' && limits.live.current >= limits.live.max) {
        setError(`Limite atteinte : vous ne pouvez avoir que ${limits.live.max} compte live actif`);
        setSaving(false);
        return;
      }
      if (formData.account_type === 'paper' && limits.paper.current >= limits.paper.max) {
        setError(`Limite atteinte : vous ne pouvez avoir que ${limits.paper.max} comptes paper actifs maximum`);
        setSaving(false);
        return;
      }
    }

    try {
      const payload = { ...formData };
      if (editingConfig) {
        payload.id = editingConfig;
      }
      // Convertir max_spend_amount: vide → null (illimité), sinon nombre
      payload.max_spend_amount = formData.max_spend_amount !== '' && formData.max_spend_amount !== null
        ? parseFloat(formData.max_spend_amount)
        : null;

      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/alpaca/config`, {
        method: editingConfig ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(editingConfig ? 'Sous-compte mis à jour avec succès !' : 'Sous-compte créé avec succès !');
        // Réinitialiser le formulaire
        handleCancel();
        // Recharger la liste
        setTimeout(() => {
          loadConfigs();
        }, 500);
      } else {
        setError(data.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce sous-compte ?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/alpaca/config/${configId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Sous-compte supprimé avec succès');
        setTimeout(() => {
          loadConfigs();
        }, 500);
      } else {
        setError(data.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="alpaca-config-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="alpaca-config-container">
      <div className="alpaca-config-header">
        <h1>Configuration Alpaca - Sous-comptes</h1>
        <p>Gérez vos sous-comptes Alpaca (1 live + 3 paper maximum)</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span className="alert-message">{error}</span>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <span className="alert-message">{success}</span>
        </div>
      )}

      {/* Liste des sous-comptes */}
      <div className="alpaca-config-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Mes sous-comptes ({configs.length})</h2>
          <button 
            className="btn btn-primary"
            onClick={handleNew}
            disabled={saving || (limits.live.current >= limits.live.max && limits.paper.current >= limits.paper.max)}
          >
            + Ajouter un sous-compte
          </button>
        </div>

        {configs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>Aucun sous-compte configuré</p>
            <p style={{ fontSize: '14px', marginTop: '10px' }}>
              Ajoutez votre premier sous-compte pour commencer
            </p>
          </div>
        ) : (
          <div className="sub-accounts-list">
            {configs.map((config) => (
              <div key={config.id} className="sub-account-item">
                <div className="sub-account-header">
                  <div>
                    <h3>{config.sub_account_name || `Sous-compte ${config.id}`}</h3>
                    <span className={`account-type-badge ${config.account_type}`}>
                      {config.account_type === 'live' ? '🔴 Live Trading' : '📝 Paper Trading'}
                    </span>
                  </div>
                  <div className="sub-account-actions">
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEdit(config)}
                      disabled={saving}
                    >
                      Modifier
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(config.id)}
                      disabled={saving}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
                <div className="sub-account-details">
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">API Key ID:</span>
                      <span className="info-value">{config.api_key_id.substring(0, 8)}...</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Environnement:</span>
                      <span className="info-value">
                        {config.base_url.includes('paper') ? 'Paper Trading' : 'Live Trading'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Seuil de vente (hausse):</span>
                      <span className="info-value">+{config.sell_threshold_up?.toFixed(2) ?? '10.00'}%</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Seuil de vente (baisse):</span>
                      <span className="info-value">{config.sell_threshold_down?.toFixed(2) ?? '-5.00'}%</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Source des actifs:</span>
                      <span className="info-value">
                        {config.asset_source === 'ai_signals' ? '🧠 Signaux IA' : '📈 Top Gainers'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Budget max du bot:</span>
                      <span className="info-value">
                        {config.max_spend_amount !== null && config.max_spend_amount !== undefined
                          ? `$${parseFloat(config.max_spend_amount).toFixed(2)}`
                          : 'Illimité'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Dernière mise à jour:</span>
                      <span className="info-value">
                        {new Date(config.updated_at).toLocaleString('fr-FR')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Indicateur des limites */}
        <div className="limits-info">
          <p>
            <strong>Limites:</strong> Live: {limits.live.current}/{limits.live.max} | 
            Paper: {limits.paper.current}/{limits.paper.max}
          </p>
        </div>
      </div>

      {/* Formulaire d'ajout/modification */}
      {showForm && (
        <div className="alpaca-config-card" style={{ marginTop: '20px' }}>
          <h2>{editingConfig ? 'Modifier un sous-compte' : 'Ajouter un nouveau sous-compte'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="account_type">Type de compte</label>
              <select
                id="account_type"
                name="account_type"
                value={formData.account_type}
                onChange={handleChange}
                required
                disabled={editingConfig !== null} // Ne pas permettre de changer le type lors de l'édition
              >
                <option value="paper">Paper Trading (Test)</option>
                <option value="live">Live Trading (Production)</option>
              </select>
              <small className="form-help">
                {formData.account_type === 'live' 
                  ? 'Live Trading utilise de l\'argent réel. ⚠️ Attention !'
                  : 'Paper Trading est recommandé pour les tests.'}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="sub_account_name">Nom du sous-compte</label>
              <input
                type="text"
                id="sub_account_name"
                name="sub_account_name"
                value={formData.sub_account_name}
                onChange={handleChange}
                placeholder={formData.account_type === 'live' ? 'Live Principal' : 'Paper 1, Paper 2, etc.'}
              />
              <small className="form-help">
                Laissez vide pour un nom automatique
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="api_key_id">API Key ID</label>
              <input
                type="text"
                id="api_key_id"
                name="api_key_id"
                value={formData.api_key_id}
                onChange={handleChange}
                placeholder="Votre API Key ID Alpaca"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="api_secret_key">API Secret Key</label>
              <input
                type="password"
                id="api_secret_key"
                name="api_secret_key"
                value={formData.api_secret_key}
                onChange={handleChange}
                placeholder={editingConfig ? "Laisser vide pour conserver la clé actuelle" : "Votre API Secret Key Alpaca"}
                required={!editingConfig}
              />
              <small className="form-help">
                {editingConfig 
                  ? "Laisser vide pour conserver la clé secrète actuelle, ou saisir une nouvelle clé pour la modifier"
                  : "Votre clé secrète ne sera jamais affichée après l'enregistrement"}
              </small>
            </div>

            <div className="form-section-divider">
              <h3>Seuils de vente du bot</h3>
            </div>

            <div className="form-group">
              <label htmlFor="sell_threshold_up">Seuil de vente en hausse (%)</label>
              <input
                type="number"
                id="sell_threshold_up"
                name="sell_threshold_up"
                value={formData.sell_threshold_up}
                onChange={handleChange}
                min="0.01"
                max="100"
                step="0.01"
                placeholder="10.00"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="sell_threshold_down">Seuil de vente en baisse (%)</label>
              <input
                type="number"
                id="sell_threshold_down"
                name="sell_threshold_down"
                value={formData.sell_threshold_down}
                onChange={handleChange}
                min="-100"
                max="-0.01"
                step="0.01"
                placeholder="-5.00"
                required
              />
            </div>

            <div className="form-section-divider">
              <h3>Stratégie d'achat</h3>
            </div>

            <div className="form-group">
              <label htmlFor="asset_source">Source des actifs à acheter</label>
              <select
                id="asset_source"
                name="asset_source"
                value={formData.asset_source}
                onChange={handleChange}
              >
                <option value="top_gainers">📈 Top Gainers (classique — meilleurs gains sur 15 jours)</option>
                <option value="ai_signals">🧠 Signaux IA (Mistral-Nemo + actualités Finnhub)</option>
              </select>
              <small className="form-help">
                <strong>Top Gainers :</strong> achète les actions avec la meilleure performance sur 15 jours.<br />
                <strong>Signaux IA :</strong> achète selon les recommandations de l'IA basées sur les actualités du jour. Bascule automatiquement sur les Top Gainers si aucun signal IA n'est disponible.
              </small>
            </div>

            <div className="form-section-divider">
              <h3>Budget du bot</h3>
            </div>

            <div className="form-group">
              <label htmlFor="max_spend_amount">Montant maximum à dépenser ($)</label>
              <input
                type="number"
                id="max_spend_amount"
                name="max_spend_amount"
                value={formData.max_spend_amount}
                onChange={handleChange}
                min="0.01"
                step="0.01"
                placeholder="Laisser vide pour illimité"
              />
              <small className="form-help">
                Montant total maximum que ce bot peut dépenser en positions ouvertes. Laisser vide pour aucune limite. Ce paramètre peut être modifié en cours d'exécution.
              </small>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Enregistrement...' : (editingConfig ? 'Mettre à jour' : 'Créer')}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="alpaca-config-help">
        <h3>Comment obtenir vos identifiants Alpaca ?</h3>
        <ol>
          <li>Connectez-vous à votre compte Alpaca sur <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer">alpaca.markets</a></li>
          <li>Allez dans la section "API Keys" de votre tableau de bord</li>
          <li>Créez une nouvelle clé API ou utilisez une clé existante</li>
          <li>Copiez l'API Key ID et l'API Secret Key</li>
          <li>Saisissez-les dans le formulaire ci-dessus</li>
        </ol>
        <p className="warning">
          <strong>⚠️ Attention :</strong> Ne partagez jamais vos clés API avec d'autres personnes. 
          Elles donnent accès complet à votre compte Alpaca.
        </p>
      </div>
    </div>
  );
};

export default AlpacaConfig;
